import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { RefusedError } from '../../api/client'
import { onboardingApi } from '../../api/onboarding'
import { outreachApi } from '../../api/outreach'
import { useSpoke } from '../../hooks/useSpoke'
import { formatRelative } from '../../lib/format'
import { scopeWords } from '../../lib/orgs'
import type { BatchView, StagedContact } from '../../types/onboarding'
import { Glance } from './Glance'
import { BenchmarkPanel, ReconciliationPanel, bandWord } from './Reconciliation'
import './onboarding.css'

// ONBOARD CONTACTS. The Import Data screen lands people immediately; this one does not. An upload is
// a batch of proposals a person works through, and nothing reaches the contacts until Commit. Every
// row says what Scout would do, what it already knows about the person, and — the reason the screen
// exists — whether the firm already has a TWO-WAY relationship with them.

export function Onboarding() {
  const spoke = useSpoke()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const home = useQuery({ queryKey: ['onboarding', spoke], queryFn: () => onboardingApi.home(spoke) })
  const inputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)

  const upload = useMutation({
    mutationFn: (file: File) => onboardingApi.upload(spoke, file, setProgress),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['onboarding', spoke] })
      navigate(`/jobs/${encodeURIComponent(r.jobId)}`)
    },
  })

  const pick = (file?: File) => {
    if (!file) return
    setFileName(file.name)
    setProgress(0)
    upload.mutate(file)
  }

  const recentDays = home.data?.bilateralRecentDays ?? 90
  const batches = home.data?.batches ?? []
  const open = batches.filter((b) => b.status !== 'committed')

  return (
    <section className="page onboarding">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · clean and confirm before anything lands{scopeWords(home.data?.scope) ? ` · ${scopeWords(home.data?.scope)}` : ''}</p>
          <h1>Onboard Contacts</h1>
          <p className="lede">
            Upload an Excel workbook or a LinkedIn Connections export. Scout cleans it, folds the duplicates together,
            matches every row against what it already knows, and flags anyone the firm already has a two-way
            relationship with. You confirm each one. <strong>Nothing reaches your contacts until you commit.</strong>
          </p>
        </div>
      </header>

      <div className="card upload-card">
        <div className="path-head"><div><p className="kicker">Step one</p><h2>Upload the list</h2></div></div>
        <p>
          A <code>.xlsx</code>, <code>.xls</code> or <code>.csv</code>. Scout finds the header row itself, so a title
          and a blank line above it are fine, and it keeps every column it cannot place — nothing in your file is
          dropped without you seeing it.
        </p>
        <div
          className="dropzone"
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files?.[0]) }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            hidden
            onChange={(e) => { pick(e.target.files?.[0]); e.currentTarget.value = '' }}
          />
          {upload.isPending
            ? <><strong>Uploading {fileName}…</strong><span className="muted">{Math.round(progress * 100)}%</span></>
            : <><strong>Drop a contact list or click to choose</strong><span className="muted">Excel workbook · LinkedIn Connections.csv · CRM export</span></>}
        </div>
        {upload.isError && <p className="error">{upload.error instanceof RefusedError ? upload.error.refusal.message : upload.error.message}</p>}
        <p className="muted small">
          Recent two-way contact means within {recentDays} days — a reply of yours in a thread with them, or a meeting.
          Scout reads your Sent Items, Deleted Items and Archive, never your Inbox, so a message you sent that was a
          reply is how it knows they wrote first. A list you blasted and nobody answered is not a relationship, and is
          never flagged as one.
        </p>
      </div>

      {home.isSuccess && open.length > 0 && (
        <div className="card">
          <h2>Waiting on you</h2>
          <ul className="plain batch-list">
            {open.map((b) => (
              <li key={b.batchId}>
                <Link to={`/onboarding/${encodeURIComponent(b.batchId)}`}>{b.fileName}</Link>
                <span className="muted small">
                  {' · '}{b.staged.toLocaleString()} staged{b.duplicates > 0 ? `, ${b.duplicates} duplicate${b.duplicates === 1 ? '' : 's'} folded in` : ''}
                  {' · '}{formatRelative(b.createdAt)}
                  {b.status === 'staging' && <span className="tag">still staging</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {home.isSuccess && batches.some((b) => b.status === 'committed') && (
        <details className="card recent-jobs">
          <summary><h2>Imports already committed</h2><span className="muted">{batches.filter((b) => b.status === 'committed').length}</span></summary>
          <ul className="plain">
            {batches.filter((b) => b.status === 'committed').map((b) => (
              <li key={b.batchId}>
                <Link to={`/onboarding/${encodeURIComponent(b.batchId)}`}>{b.fileName}</Link>
                <span className="muted small">
                  {' · '}{b.committed.toLocaleString()} landed{b.rejected > 0 ? `, ${b.rejected} turned down` : ''}
                  {b.committedAt ? ` · ${formatRelative(b.committedAt)}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {home.isError && <p className="error">{home.error instanceof RefusedError ? home.error.refusal.message : home.error.message}</p>}
    </section>
  )
}

/* ── The review ────────────────────────────────────────────────────────────────────────────────── */

type Filter = 'all' | 'flagged' | 'new' | 'merge' | 'conflict' | 'skip' | 'accepted' | 'rejected'

export function OnboardingReview() {
  const { batchId = '' } = useParams<{ batchId: string }>()
  const spoke = useSpoke()
  const qc = useQueryClient()
  const [filter, setFilter] = useState<Filter>('all')
  const [committed, setCommitted] = useState<string | null>(null)

  const home = useQuery({ queryKey: ['onboarding', spoke], queryFn: () => onboardingApi.home(spoke) })
  const q = useQuery({ queryKey: ['onboarding-batch', spoke, batchId], queryFn: () => onboardingApi.batch(spoke, batchId) })
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['onboarding-batch', spoke, batchId] })
    void qc.invalidateQueries({ queryKey: ['onboarding', spoke] })
  }

  const bulk = useMutation({ mutationFn: () => onboardingApi.acceptUnambiguous(spoke, batchId), onSuccess: refresh })
  const commit = useMutation({
    mutationFn: () => onboardingApi.commit(spoke, batchId),
    onSuccess: (r) => { setCommitted(r.message); refresh() },
  })

  const view: BatchView | undefined = q.data
  const shown = useMemo(() => filterRows(view?.contacts ?? [], filter), [view?.contacts, filter])
  // his own workbook: the reconciliation and the benchmark sit above the rows, and read before and after commit
  const priority = view?.batch.source === 'priority-contacts' && view.batch.status !== 'staging'
  const reconciliation = useQuery({ queryKey: ['onboarding-reconciliation', spoke, batchId], queryFn: () => onboardingApi.reconciliation(spoke, batchId), enabled: !!priority })
  const benchmark = useQuery({ queryKey: ['outreach-benchmark', spoke, batchId], queryFn: () => outreachApi.benchmark(spoke, batchId), enabled: !!priority })

  if (q.isPending) return <p className="muted centered">Loading…</p>
  if (q.isError) {
    const message = q.error instanceof RefusedError ? q.error.refusal.message : q.error.message
    return (
      <section className="page onboarding">
        <p className="kicker"><Link to="/onboarding">Onboard Contacts</Link></p>
        <h1>That import is not here</h1>
        <div className="refusal"><strong>No such import</strong><p>{message}</p></div>
      </section>
    )
  }
  if (!view) return null

  const recentDays = home.data?.bilateralRecentDays ?? 90
  const staging = view.batch.status === 'staging'
  const done = view.batch.status === 'committed'
  const canCommit = view.counts.accepted > 0 && !done

  return (
    <section className="page onboarding review">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/onboarding">Onboard Contacts</Link> · {view.batch.fileName}</p>
          <h1>{done ? 'Imported' : 'Confirm before it lands'}</h1>
          <p className="lede">
            {done
              ? `${view.batch.committed.toLocaleString()} contact${view.batch.committed === 1 ? '' : 's'} landed from this file${view.batch.rejected > 0 ? `, ${view.batch.rejected} turned down with a reason` : ''}.`
              : 'Every row below is a proposal. Accept the ones you want, turn down the ones you do not (with the reason, so the next import learns), and nothing moves until you commit.'}
          </p>
        </div>
        <div className="head-actions">
          {!done && <button className="button" onClick={() => bulk.mutate()} disabled={bulk.isPending || staging}>{bulk.isPending ? 'Accepting…' : 'Accept the unambiguous'}</button>}
          {!done && <button className="button primary" onClick={() => commit.mutate()} disabled={!canCommit || commit.isPending}>{commit.isPending ? 'Committing…' : `Commit ${view.counts.accepted.toLocaleString()}`}</button>}
          {(done || view.counts.accepted > 0) && (
            <a className="button" href={onboardingApi.exportUrl(spoke, batchId)}>Export for {home.data?.sinkLabel ?? 'Intapp'}</a>
          )}
        </div>
      </header>

      {staging && (
        <div className="card">
          <p>This import is still being staged{view.activeJobId ? <> — <Link to={`/jobs/${encodeURIComponent(view.activeJobId)}`}>watch the job</Link></> : ''}.</p>
        </div>
      )}
      {committed && <div className="card committed-note"><strong>Committed.</strong><p>{committed}</p></div>}
      {commit.isError && <p className="error">{commit.error instanceof RefusedError ? commit.error.refusal.message : commit.error.message}</p>}

      <Glance batch={view.batch} counts={view.counts} contacts={view.contacts} recentDays={recentDays} onPick={(f) => setFilter(f as Filter)} />

      {priority && reconciliation.isSuccess && <ReconciliationPanel r={reconciliation.data} />}
      {priority && reconciliation.isError && <p className="error">{reconciliation.error instanceof RefusedError ? reconciliation.error.refusal.message : reconciliation.error.message}</p>}
      {priority && benchmark.isSuccess && <BenchmarkPanel b={benchmark.data} />}

      <nav className="filters" aria-label="Filter the staged contacts">
        {(['all', 'flagged', 'new', 'merge', 'conflict', 'skip', 'accepted', 'rejected'] as Filter[]).map((f) => (
          <button key={f} className={filter === f ? 'chip-button active' : 'chip-button'} onClick={() => setFilter(f)}>
            {f} <em>{filterRows(view.contacts, f).length}</em>
          </button>
        ))}
      </nav>

      <ul className="plain staged-rows">
        {shown.length === 0 && <li className="muted">Nothing under this filter.</li>}
        {shown.map((c) => <StagedRow key={c.stagedContactId} contact={c} batchId={batchId} readOnly={done} onChanged={refresh} />)}
      </ul>

      <p className="muted small export-note">
        The direct push into Intapp/DealCloud waits on their API access (PL-005). Until it lands, the confirmed set
        exports as a workbook in Intapp's own column order; when the API opens, the same button sends it straight
        through and nothing else on this screen changes.
      </p>
    </section>
  )
}

function filterRows(rows: StagedContact[], filter: Filter): StagedContact[] {
  switch (filter) {
    case 'flagged': return rows.filter((r) => r.flag.flagged)
    case 'accepted': return rows.filter((r) => r.status === 'accepted' || r.status === 'committed')
    case 'rejected': return rows.filter((r) => r.status === 'rejected')
    case 'all': return rows
    default: return rows.filter((r) => r.action === filter)
  }
}

function StagedRow({ contact, batchId, readOnly, onChanged }: {
  contact: StagedContact; batchId: string; readOnly: boolean; onChanged: () => void
}) {
  const spoke = useSpoke()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const accept = useMutation({ mutationFn: () => onboardingApi.accept(spoke, batchId, contact.stagedContactId), onSuccess: onChanged })
  const reject = useMutation({
    mutationFn: () => onboardingApi.reject(spoke, batchId, contact.stagedContactId, reason),
    onSuccess: () => { setRejecting(false); setReason(''); onChanged() },
  })
  const extras = Object.entries(contact.extras)

  return (
    <li className={`staged-row ${contact.status} ${contact.flag.flagged ? 'flagged' : ''}`}>
      <div className="staged-main">
        <div className="staged-who">
          <strong>{contact.name}</strong>
          <span className="muted">
            {contact.email ?? 'no address in the file'}
            {contact.title ? ` · ${contact.title}` : ''}
            {contact.companyName ? ` · ${contact.companyName}` : ''}
          </span>
        </div>
        <div className="staged-chips">
          <span className={`tag action-${contact.action}`}>{contact.action}</span>
          {contact.mark && (
            <span className="tag your-band" title={contact.mark.bandLabel ?? undefined}>
              {contact.mark.target ? `target${contact.mark.segment ? ` · ${contact.mark.segment}` : ''}` : `your band: ${bandWord(contact.mark.band)}${contact.mark.cadenceDays ? ` · every ${contact.mark.cadenceDays} days` : ''}`}
            </span>
          )}
          {contact.nameNote && <span className="tag name-note" title="Your file gave no name here; nothing is guessed silently">{contact.nameNote}</span>}
          {contact.flag.flagged && <span className={`tag band-${contact.flag.band}`}>{contact.flag.band}</span>}
          {!contact.flag.flagged && contact.flag.band !== 'none' && <span className="tag band-quiet">{contact.flag.band}</span>}
          {contact.publicMail && <span className="tag public-mail" title="A personal mail provider names nobody's company">personal address</span>}
          {contact.status !== 'pending' && <span className={`tag status-${contact.status}`}>{contact.status}</span>}
        </div>
      </div>

      <p className={`staged-why ${contact.flag.flagged ? 'strong' : 'muted'}`}>{contact.flag.why}</p>

      {contact.colleagues.length > 0 && (
        <ul className="plain colleagues">
          {contact.colleagues.map((t) => (
            <li key={t.ownerId}>
              <span className="tie-strength" aria-hidden="true" style={{ width: `${Math.round(Math.min(1, t.strength) * 100)}%` }} />
              <span><strong>Someone else at the firm holds this tie.</strong> {t.why}</span>
            </li>
          ))}
        </ul>
      )}

      {contact.diff.length > 0 && (
        <table className="diff">
          <thead><tr><th>field</th><th>on file</th><th>this file</th><th>wins</th></tr></thead>
          <tbody>
            {contact.diff.map((d) => (
              <tr key={d.field}>
                <td>{d.field}</td>
                <td className={d.winner === 'on-file' ? 'wins' : ''}>{d.onFile ?? <em className="muted">blank</em>}</td>
                <td className={d.winner === 'incoming' ? 'wins' : ''}>{d.incoming ?? <em className="muted">blank</em>}</td>
                <td className="muted">{d.winner === 'on-file' ? 'what is on file' : 'the file'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {extras.length > 0 && (
        <details className="extras">
          <summary className="muted small">{extras.length} column{extras.length === 1 ? '' : 's'} Scout could not place, kept verbatim</summary>
          <dl>{extras.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </details>
      )}

      {contact.mark && (contact.mark.yourMark || contact.mark.note || contact.mark.employerFlag) && (
        <p className="muted small">
          {contact.mark.yourMark && <>Your mark: {contact.mark.yourMark}. </>}
          {contact.mark.note && <>Your note: {contact.mark.note}. </>}
          {contact.mark.employerFlag && <>Employer: {contact.mark.employerFlag}</>}
        </p>
      )}
      {contact.rejectReason && <p className="muted small">Turned down: {contact.rejectReason}</p>}

      {!readOnly && contact.status !== 'committed' && (
        <div className="staged-actions">
          {contact.status !== 'accepted' && (
            <button className="button small" onClick={() => accept.mutate()} disabled={accept.isPending}>
              {accept.isPending ? 'Accepting…' : contact.action === 'skip' ? 'Accept (nothing changes)' : 'Accept'}
            </button>
          )}
          {contact.status !== 'rejected' && !rejecting && <button className="link" onClick={() => setRejecting(true)}>Turn down…</button>}
          {rejecting && (
            <form
              className="reject-form"
              onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 3) reject.mutate() }}
            >
              <label htmlFor={`why-${contact.stagedContactId}`} className="muted small">Why? The reason is what the next import learns from.</label>
              <input
                id={`why-${contact.stagedContactId}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Bought list — she has never replied and does not know us."
                autoFocus
              />
              <button className="button small" type="submit" disabled={reason.trim().length < 3 || reject.isPending}>
                {reject.isPending ? 'Recording…' : 'Turn it down'}
              </button>
              <button className="link" type="button" onClick={() => { setRejecting(false); setReason('') }}>Cancel</button>
            </form>
          )}
          {accept.isError && <span className="error">{accept.error instanceof RefusedError ? accept.error.refusal.message : accept.error.message}</span>}
          {reject.isError && <span className="error">{reject.error instanceof RefusedError ? reject.error.refusal.message : reject.error.message}</span>}
        </div>
      )}
    </li>
  )
}
